import {
  Page,
  Document,
  PDFViewer,
  Image,
  View,
  Text,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import { Request } from "../../hooks/Request";
import { numberWithDecimals, roundUpCustom } from "../../utils/validate";
import { useEffect, useMemo } from "react";
import {
  formatCodigoCotizacion,
  formatCodigoInterno,
  formattedValue,
  formattedValueNumber,
} from "../../utils/utils";

const styles = StyleSheet.create({
  page: {
    padding: "20px 10px",
    fontSize: 7.8,
    paddingBottom: 80,
  },
  section: {
    marginTop: 10,
    marginHorizontal: 30,
  },
  title: {
    fontSize: 13,
    textAlign: "right",
    marginBottom: 10,
    color: "#404040",
  },
  text1: { fontWeight: "bold", marginBottom: 1, paddingLeft: 2, paddingTop: 2 },
  text2: { marginBottom: 1, paddingLeft: 2 },
  row: {
    flexDirection: "row",
    gap: 80,
  },
  rows: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  label: {
    width: 100,
    textAlign: "right",
    marginTop: 1,
    fontWeight: "semibold",
  },
  value: {
    flex: 1,
    wordBreak: "break-word",
    textAlign: "right",
    marginTop: 1,
    fontWeight: "semibold",
  },
  column: {
    flex: 1,
    color: "#404040",
    fontSize: 7,
  },
  header: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 5,
  },
  table: {
    marginTop: 10,
    borderColor: "#b0b0b0",
    borderStyle: "solid",
  },
  tableRowHeader: {
    flexDirection: "row",
    borderBottom: 1,
    borderTop: 1,
    borderColor: "#b0b0b0",
    borderStyle: "solid",
  },
  tableRow: {
    flexDirection: "row",
    minHeight: 12,
    borderColor: "#b0b0b0",
    borderStyle: "solid",
  },
  cellDescripcion: {
    flex: 2.2,
    padding: 3,
    fontSize: 7.5,
    borderColor: "#b0b0b0",
    borderStyle: "solid",
  },
  cell: {
    flex: 1,
    padding: 3,
    fontSize: 7.5,
    textAlign: "right",
    borderColor: "#b0b0b0",
    borderStyle: "solid",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    textAlign: "center",
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 5,
  },
  footerText: {
    fontSize: 8,
  },
  //--
  contenedorTabla: {
    display: "flex",
    width: "40%",
    borderStyle: "solid",
    borderWidth: 1.5,
    borderColor: "#dbdbdb",
    marginBottom: 10,
    margin: "auto",
  },
  filaTabla: {
    flexDirection: "row",
  },
  celdaEncabezado: {
    flex: 1,
    fontWeight: "bold",
    padding: 3,
    borderBottom: "1.5px solid #dbdbdb",
    textAlign: "center",
  },
  celdaDatosIzquierda: {
    flex: 2,
    padding: 3,
    borderBottom: "1.5px solid #dbdbdb",
    fontWeight: "normal",
  },
  celdaDatosDerecha: {
    flex: 1,
    padding: 3,
    borderBottom: "1.5px solid #dbdbdb",
    borderLeft: "1.5px solid #dbdbdb",
    textAlign: "right",
    fontWeight: "normal",
  },
  celdaTotalIzquierda: {
    flex: 2,
    padding: 3,
    borderBottom: "1.5px solid #dbdbdb",
    borderRight: "1.5px solid #dbdbdb",
    textAlign: "right",
    fontWeight: "bold",
  },
  celdaTotalDerecha: {
    flex: 1,
    borderBottom: "1.5px solid #dbdbdb",
    textAlign: "center",
    fontWeight: "bold",
  },
  celdaLiquidacion: {
    flex: 1,
    padding: 3,
    borderRight: "1.5px solid #dbdbdb",
    borderBottom: "1.5px solid #dbdbdb",
    textAlign: "center",
  },
  celdaLiquidacionDerecha: {
    flex: 1,
    padding: 3,
    borderBottom: "1.5px solid #dbdbdb",
    textAlign: "right",
  },
  celdaLiquidacionHeader: {
    flex: 1,
    padding: 3,
    fontWeight: "normal",
    borderBottom: "1.5px solid #dbdbdb",
    borderRight: "1.5px solid #dbdbdb",
    textAlign: "center",
  },
  celdaLiquidacionHeaderFinal: {
    flex: 1,
    padding: 3,
    fontWeight: "normal",
    borderBottom: "1.5px solid #dbdbdb",
    textAlign: "center",
  },
  celdaPieTabla: {
    flex: 3,
    padding: 3,
    borderBottom: "1.5px solid #dbdbdb",
    borderRight: "1.5px solid #dbdbdb",
    fontWeight: "bold",
    textAlign: "right",
  },
  semibold: {
    fontWeight: "semibold",
  },
});

const listFields = [
  "codigoDeInterno",
  "nroCotizacion",
  "modalidadDeDespacho",
  "descripciónDeLaMercancía",
  "partidaArancelaria",
  "valoresNominales",
  //
  "fechaDeCotización",
  "tipoDeDespacho",
  "a4AduanaDeDespacho",
  "proveedor",
  "nroFacturaComercial",
  "piNro",
  "nroBl",
  "tipoDeCambioProforma",
  "nroDeContenedor",
  //
  "a-TotalTributos",
  "b-TotalTributos",
  "c-TotalTributos",
  "porcentajeParaCalcularIva",
  "notaDeLaCotizacion",
];

const CustomsClearanceQuote = (props) => {
  const { procedure, notesValueData = [] } = props;
  const { data, loading } = Request({
    urlApi: `/Procedure/${
      procedure.id || procedure.procedureId
    }/dataValues?${listFields.map((field) => `fieldnames=${field}`).join("&")}`,
  });

  const codigoCotizacion = useMemo(() => {
    if (!data || !data.codigoDeInterno) return "-";
    return formatCodigoCotizacion(data.codigoDeInterno);
  }, [data]);

  const codigoInterno = useMemo(() => {
    if (!data || !data.codigoDeInterno) return "-";
    return formatCodigoInterno(data.codigoDeInterno);
  }, [data]);

  if (!loading || !data) {
    return null;
  }

  const calculateTotals = (data) => {
    const totalA =
      data["a-TotalTributosSubDatas"]?.reduce((sum, item) => {
        if (!item) return sum;
        // En el modo multiple, el total de la fila es (Impuesto * Cantidad)
        const totalItem =
          Number(item.description || 0) * Number(item.description3 || 1);
        return sum + totalItem;
      }, 0) || 0;

    const totalB =
      data["b-TotalTributosSubDatas"]?.reduce(
        (sum, item) => sum + (parseFloat(item.description2) || 0),
        0,
      ) || 0;

    const totalC =
      data["c-TotalTributosSubDatas"]?.reduce(
        (sum, item) => sum + (parseFloat(item.description2) || 0),
        0,
      ) || 0;

    const totalGeneral = totalA + totalB + totalC;

    return {
      totalA,
      totalB,
      totalC,
      totalGeneral,
    };
  };

  const totales = calculateTotals(data);

  const MyDoc = ({ data }) => {
    const aItems = data["a-TotalTributosSubDatas"] || [];
    const bItems = data["b-TotalTributosSubDatas"] || [];
    const cItems = data["c-TotalTributosSubDatas"] || [];

    // Obtener el total en dólares de las notas
    const totalDescription = notesValueData.reduce(
      (acum, item) => acum + parseFloat(item.description || 0),
      0,
    );
    // Obtener T/C, usando 6.96 si no existe
    const tipoCambio = parseFloat(
      notesValueData.find((item) => item.label === "T/C")?.description2 || 6.96,
    );
    const totalBs = roundUpCustom(totalDescription * tipoCambio);

    // Obtener porcentajes
    const ga = parseFloat(
      notesValueData.find((item) => item.label === "Porcentaje GA")
        ?.description2 || 10,
    );
    const iva = parseFloat(
      notesValueData.find((item) => item.label === "Porcentaje IVA")
        ?.description2 || 14.94,
    );
    const ice = parseFloat(
      notesValueData.find((item) => item.label === "Porcentaje ICE")
        ?.description2 || 0,
    );

    // Calcular montos para el modo normal
    const montoGA = roundUpCustom((totalBs * ga) / 100);
    const montoICE = roundUpCustom((totalBs * ice) / 100);
    const baseIVA = totalBs + montoGA + montoICE;
    const montoIVA = roundUpCustom(baseIVA * (iva / 100));

    const isValid = (val) => parseFloat(val) > 0;

    const impuestosNotas = [
      isValid(ga) && {
        label: "Gravamen Arancelario - GA",
        porcentaje: ga.toFixed(2),
        importe: totalBs,
        impuesto: montoGA,
        total: montoGA,
      },
      isValid(iva) && {
        label: "Impuesto al valor Agregado - IVA",
        porcentaje: iva.toFixed(2),
        importe: baseIVA,
        impuesto: montoIVA,
        total: montoIVA,
      },
      isValid(ice) && {
        label: "Impuesto a los Consumos Específicos - ICE",
        porcentaje: ice.toFixed(2),
        importe: totalBs,
        impuesto: montoICE,
        total: montoICE,
      },
      {
        label: "Formulario Digital",
        porcentaje: "0.00",
        importe: 100,
        impuesto: 0.0,
        total: 100,
      },
    ].filter(Boolean);

    // Mapa para combinar con los items de la tabla A (modo normal)
    const itemsMap = new Map();

    impuestosNotas.forEach((item) => {
      if (item) {
        itemsMap.set(item.label.trim().toLowerCase(), { ...item });
      }
    });

    // Si hay items en aItems (modo multiple) y estamos en modo normal, reemplazar con los cálculos
    // Pero en modo normal, los aItems pueden venir vacíos o con datos, pero usamos nuestros cálculos.
    // En modo multiple, se usan los aItems.

    // Para el modo normal, usamos impuestosNotas, para el modo multiple, usamos aItems.

    // Calcular total de la tabla A en modo normal
    const totalPorcentajes = impuestosNotas.reduce(
      (acum, item) => acum + item.total,
      0,
    );

    return (
      <Document>
        <Page size="LETTER" style={styles.page}>
          <View style={styles.section}>
            <Text
              style={{ ...styles.title, marginTop: "10px", fontWeight: "600" }}
            >
              COTIZACION DE DESPACHO{"\n"}ADUANERO
            </Text>
            <View style={styles.row}>
              <View style={{ ...styles.column, marginTop: 0 }}>
                <Text
                  style={{
                    ...styles.text1,
                    backgroundColor: "#F2F2F2",
                    paddingBottom: 0,
                  }}
                >
                  Cliente
                </Text>
                <Text style={[{ ...styles.text2 }]}>
                  {data.company.razonSocial}
                </Text>
                <Text style={styles.text1}>Nit:</Text>
                <Text style={styles.text2}>{data.company.nit}</Text>
                <Text style={styles.text1}>Direccion:</Text>
                <Text style={styles.text2}>{data.company.direccion}</Text>
                <Text style={styles.text1}>Ciudad:</Text>
                <Text style={styles.text2}>{data.company.ciudad}</Text>
                <Text style={styles.text1}>Contacto:</Text>
                <Text style={styles.text2}>{data.company.contacto}</Text>
              </View>
              <View style={styles.column}>
                <View style={styles.rows}>
                  <Text style={styles.label}>Numero:</Text>
                  <Text style={styles.value}>
                    {/* {codigoInterno} */}
                    {data.procNumberString
                      ? formattedValue(data.procNumberString)
                      : "-"}
                  </Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>Fecha:</Text>
                  <Text style={styles.value}>{data.fechaDeCotización}</Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>Cotización:</Text>
                  <Text style={styles.value}>
                    {data.procNumberString
                      ? formattedValueNumber(data.procNumberString)
                      : "-"}
                  </Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>Despacho:</Text>
                  <Text style={styles.value}>{data.tipoDeDespacho}</Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>Cod. de Aduana:</Text>
                  <Text style={styles.value}>{data.a4AduanaDeDespacho}</Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>Proveedor:</Text>
                  <Text style={styles.value}>{data.proveedor}</Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>N° Factura comercial:</Text>
                  <Text style={styles.value}>{data.nroFacturaComercial}</Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>P/I N°:</Text>
                  <Text style={styles.value}>{data.piNro}</Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>Doc. Embarque:</Text>
                  <Text style={styles.value}>{data.nroBl}</Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>Contenedor N°:</Text>
                  <Text style={styles.value}>{data.nroDeContenedor}</Text>
                </View>
                <View style={styles.rows}>
                  <Text style={styles.label}>T/C:</Text>
                  <Text style={styles.value}>{data.tipoDeCambioProforma}</Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.section}>
            {/* tabla A */}
            <View style={styles.table}>
              <View style={styles.tableRowHeader}>
                <Text style={styles.cellDescripcion}>
                  <Text style={[styles.semibold, { fontSize: 8.5 }]}>
                    a) Descripción{" "}
                  </Text>
                  (impuestos de aduana)
                </Text>
                <Text style={[styles.cell, styles.semibold]}>Importe Bs.</Text>
                <Text style={[styles.cell, styles.semibold]}>Cantidad</Text>
                <Text style={[styles.cell, styles.semibold]}>Impuestos</Text>
                <Text style={[styles.cell, styles.semibold]}>Total Bs.</Text>
              </View>
              {data.category === "multiple"
                ? aItems.concat([...Array(0)]).map((item, index) => {
                    let notaValor = null;
                    let baseImponible = null;
                    let tasa = null;
                    let importeCalc = null;

                    try {
                      const str = item?.description2?.trim();
                      if (str?.startsWith("{") && str.endsWith("}")) {
                        const parsed = JSON.parse(str);
                        notaValor = parsed.notaValor;
                        baseImponible =
                          parsed.liquidacionTributos?.ga?.baseImponible || 0;
                        tasa = parsed.liquidacionTributos?.ga?.tasa || 0;
                        importeCalc = 0.0; //baseImponible * (tasa / 100);
                      }
                    } catch (e) {
                      // silenciar o log
                    }
                    return (
                      <View
                        key={index}
                        style={{
                          ...styles.tableRow,
                          backgroundColor: index % 2 === 0 ? "#fff" : "#f0f0f0",
                        }}
                      >
                        <Text style={styles.cellDescripcion}>
                          {item?.label
                            ? item.label + "" // (item?.description4 ? " " + item.description4 : "")   para concatenar modelo a la moto
                            : ""}
                        </Text>
                        <Text style={styles.cell}>
                          {item
                            ? importeCalc !== null
                              ? numberWithDecimals(importeCalc)
                              : "0.00"
                            : ""}
                        </Text>
                        <Text style={styles.cell}>
                          {item?.description3 || ""}
                        </Text>
                        <Text style={styles.cell}>
                          {numberWithDecimals(Number(item?.description))}
                        </Text>
                        <Text style={styles.cell}>
                          {" "}
                          {/*(Importe + Impuesto) * Cantidad */}
                          {item
                            ? numberWithDecimals(
                                (Number(importeCalc) +
                                  Number(item?.description || 0)) *
                                  Number(item?.description3 || 0),
                              )
                            : ""}
                        </Text>
                      </View>
                    );
                  })
                : null}

              {data.category === "normal"
                ? impuestosNotas.map((item, index) => (
                    <View
                      key={index}
                      style={{
                        ...styles.tableRow,
                        backgroundColor: index % 2 === 0 ? "#fff" : "#f0f0f0",
                      }}
                    >
                      <Text style={styles.cellDescripcion}>{item?.label}</Text>
                      <Text style={styles.cell}>
                        {/* Mostrar el importe (base imponible) para GA, IVA, ICE, y 100 para Formulario Digital */}
                        {item?.label === "Formulario Digital"
                          ? numberWithDecimals(100)
                          : numberWithDecimals(Number(item?.importe || 0))}
                      </Text>
                      <Text style={styles.cell}>1,00</Text>
                      <Text style={styles.cell}>
                        {numberWithDecimals(Number(item.impuesto))}
                      </Text>
                      <Text style={styles.cell}>
                        {numberWithDecimals(Number(item.total))}
                      </Text>
                    </View>
                  ))
                : null}
            </View>
            <View style={styles.tableRowHeader}>
              <Text style={styles.cellDescripcion}> </Text>
              <Text style={styles.cell}></Text>
              <Text style={styles.cell}></Text>
              <Text style={[styles.cell, styles.semibold]}>Suma:</Text>
              <Text style={[styles.cell, styles.semibold]}>
                {data.category === "normal"
                  ? numberWithDecimals(totalPorcentajes)
                  : numberWithDecimals(totales.totalA)}
              </Text>
            </View>

            {/* Tabla B */}
            <View style={styles.table}>
              <View style={styles.tableRowHeader}>
                <Text
                  style={[
                    styles.cellDescripcion,
                    styles.semibold,
                    { fontSize: 8.5 },
                  ]}
                >
                  b) Gastos relacionados al despacho
                </Text>
                <Text style={[styles.cell, styles.semibold]}>Total Bs.</Text>
              </View>
              {(bItems.length > 0 ? bItems : [{}]).map((item, index) => (
                <View
                  key={index}
                  style={{
                    ...styles.tableRow,
                    backgroundColor: index % 2 === 0 ? "#fff" : "#f0f0f0",
                  }}
                >
                  <Text style={styles.cellDescripcion}>
                    {item?.label || ""}
                  </Text>
                  <Text style={styles.cell}>
                    {item?.description2
                      ? numberWithDecimals(Number(item.description2))
                      : ""}
                  </Text>
                </View>
              ))}
            </View>
            <View style={styles.tableRowHeader}>
              <Text style={styles.cellDescripcion}> </Text>
              <Text style={styles.cell}></Text>
              <Text style={styles.cell}></Text>
              <Text style={[styles.cell, styles.semibold]}>Suma:</Text>
              <Text style={[styles.cell, styles.semibold]}>
                {numberWithDecimals(totales.totalB)}
              </Text>
            </View>

            {/* tabla C */}
            <View style={styles.table}>
              <View style={styles.tableRowHeader}>
                <Text
                  style={[
                    styles.cellDescripcion,
                    styles.semibold,
                    { fontSize: 8.5 },
                  ]}
                >
                  c) Honorarios y Servicios
                </Text>
                <Text style={[styles.cell, styles.semibold]}>Total Bs.</Text>
              </View>
              {(cItems.length > 0 ? cItems : [{}]).map((item, index) => (
                <View
                  key={index}
                  style={{
                    ...styles.tableRow,
                    backgroundColor: index % 2 === 0 ? "#fff" : "#f0f0f0",
                  }}
                >
                  <Text style={styles.cellDescripcion}>
                    {item?.label || ""}
                  </Text>
                  <Text style={styles.cell}>
                    {numberWithDecimals(Number(item?.description2))}
                  </Text>
                </View>
              ))}
            </View>
            <View style={styles.tableRowHeader}>
              <Text style={styles.cellDescripcion}> </Text>
              <Text style={styles.cell}></Text>
              <Text style={styles.cell}></Text>
              <Text style={[styles.cell, styles.semibold]}>Suma:</Text>
              <Text style={[styles.cell, styles.semibold]}>
                {numberWithDecimals(totales.totalC)}
              </Text>
            </View>

            {/* Total General */}
            <View style={styles.table}>
              <View
                style={[
                  styles.tableRowHeader,
                  { borderBottom: "0px solid white" },
                ]}
              >
                <Text style={styles.cellDescripcion}></Text>
                <Text style={styles.cell}></Text>
                <Text style={styles.cell}></Text>
                <Text style={{ ...styles.cell, fontSize: 8.5 }}>
                  Total suma a+b+c:
                </Text>
                <Text style={{ ...styles.cell, fontSize: 8.5 }}>
                  {data.category === "multiple"
                    ? numberWithDecimals(totales.totalGeneral)
                    : numberWithDecimals(
                        Number(totalPorcentajes) +
                          Number(totales.totalB) +
                          Number(totales.totalC),
                      )}
                </Text>
              </View>
            </View>

            <View>
              <View
                style={{
                  marginTop: 14,
                  fontFamily: "Courier",
                  fontSize: 8,
                  lineHeight: 1.3,
                }}
              >
                <Text
                  style={{
                    fontWeight: "bold",
                    fontSize: 10,
                    paddingBottom: 2,
                    backgroundColor: "#f2f2f2",
                  }}
                >
                  Terminos y Condiciones
                </Text>

                <View
                  style={{
                    backgroundColor: "#fff",
                    paddingVertical: 3,
                    paddingHorizontal: 4,
                    borderRadius: 4,
                    fontWeight: "bold",
                  }}
                >
                  <Text>
                    1.- La presente cotización es para despacho{" "}
                    {data.tipoDeDespacho} ADUANA {data.a4AduanaDeDespacho}, no
                    se está considerando gastos adicionales que se presenten en
                    el despacho aduanero.
                  </Text>
                </View>

                <View
                  style={{
                    backgroundColor: "#e7f3e2",
                    paddingVertical: 3,
                    paddingHorizontal: 4,
                    borderRadius: 4,
                  }}
                >
                  <Text style={{ fontWeight: "bold" }}>
                    2.- Para el pago de los tributos Aduaneros el importador
                    podrá realizar el PAGO o depositar el monto señalado a la
                    cuenta en Bolivianos del BANCO UNION CUENTA: N°
                    10000045331464 SCZPLUS AGENCIA DESPACHANTE DE ADUANA S.R.L.
                  </Text>
                </View>
                <View
                  style={{
                    backgroundColor: "#fff",
                    paddingVertical: 3,
                    paddingHorizontal: 4,
                    borderRadius: 4,
                  }}
                >
                  <Text>
                    3.- El Cliente declara estar de acuerdo con el monto y las
                    condiciones de pago que se señalan en este documento. En
                    caso de existir observación con relación al monto, esta
                    deberá ser comunicada al correo contabilidad@g-scz.com en un
                    plazo no mayor al día dos, caso contrario se dará por
                    aceptado.
                    <Text style={{ fontWeight: "bold" }}>
                      {"\n"}SCZPLUS AGENCIA DESPACHANTE DE ADUANA S.R.L.
                    </Text>{" "}
                    <Text>
                      se encarga únicamente de las gestiones en los recintos
                      Aduaneros, por lo tanto, no es responsable por el
                      contenido, medidas, cantidad o valores de la mercadería.
                    </Text>
                  </Text>
                </View>

                <View
                  style={{
                    backgroundColor: "#fff",
                    paddingVertical: 3,
                    paddingHorizontal: 4,
                    borderRadius: 4,
                  }}
                >
                  <Text>
                    4.- SCZPLUS AGENCIA DESPACHANTE DE ADUANA S.R.L. realizara
                    los formularios DAM-DIM
                  </Text>
                </View>
                <View
                  style={{
                    backgroundColor: "#fff",
                    paddingVertical: 3,
                    paddingHorizontal: 4,
                    borderRadius: 4,
                  }}
                >
                  <Text style={{ fontWeight: "bold" }}>
                    5.- En caso de existir alguna observación por parte de la
                    administración Aduanera se notificará al importador para su
                    verificación.
                  </Text>
                </View>
                <View
                  style={{
                    backgroundColor: "#fff",
                    paddingVertical: 3,
                    paddingHorizontal: 4,
                    borderRadius: 4,
                  }}
                >
                  <Text>
                    6.- El Importador es responsable por la compra del SOAT
                  </Text>
                </View>
              </View>
            </View>

            {/* Para generar la data de la tabla Anexo para 'normal' */}
            {(() => {
              const convertNormal = data.category === "normal" ? {
                notaValor: {
                  valorFob: notesValueData.find((item) => item.label === "VALOR FOB")?.description || 0,
                  gastosOrigen: notesValueData.find((item) => item.label === "OTRAS EROGACIONES")?.description || 0, 
                  flete1: notesValueData.find((item) => item.label === "FLETES I")?.description || 0,
                  flete2: notesValueData.find((item) => item.label === "FLETES II")?.description || 0,
                  seguro: notesValueData.find((item) => item.label === "SEGURO")?.description || 0,
                  aspbGastosPuertos: notesValueData.find((item) => item.label === "GASTOS PORTUARIOS")?.description || 0,
                  otrosGastos: notesValueData.find((item) => item.label === "OTROS GASTOS")?.description || 0,
                  cifFronteraDolares: totalDescription.toFixed(2),
                  cifFronteraBolivianos: totalBs,
                },
                liquidacionTributos: {
                  ga: { baseImponible: totalBs, tasa: ga },
                  iva: { baseImponible: baseIVA, tasa: iva },
                  ice1: { baseImponible: totalBs, tasa: ice },
                  ice2: { baseImponible: 0, tasa: 0 },
                  mab: { baseImponible: 0, tasa: 0 },
                  idh: { baseImponible: 0, tasa: 0 },
                  suma: montoGA + montoIVA + montoICE,
                }
              } : null;

              const anexoConverts = data.category === "multiple" 
                ? aItems.map((item) => {
                    try { return JSON.parse(item.description2 || "{}"); } 
                    catch { return null; }
                  }).filter(Boolean)
                : data.category === "normal" ? [convertNormal] : [];

              return (data.category === "multiple" || data.category === "normal") ? (
                <>
                  <View
                    wrap={false}
                    break
                    style={{ marginTop: 30, color: "#4a4a4a" }}
                  >
                  <Text
                    style={{
                      textAlign: "center",
                      fontWeight: "semibold",
                      fontSize: 14,
                      marginBottom: 20,
                    }}
                  >
                    Anexo
                  </Text>
                  <Text
                    style={{
                      fontSize: 7,
                      fontWeight: "semibold",
                      marginBottom: 10,
                    }}
                  >
                    REGLAMENTO A LA LEY GENERAL DE ADUANAS.
                  </Text>
                  <Text
                    style={{
                      fontSize: 8,
                      lineHeight: 1.5,
                      textAlign: "justify",
                    }}
                  >
                    <Text style={{ fontWeight: "semibold", fontSize: 7 }}>
                      ARTÍCULO 20° (BASE IMPONIBLE)
                    </Text>
                    <Text style={{ fontWeight: "normal" }}>
                      {" "}
                      La base imponible sobre la cual se liquida el gravamen
                      arancelario está constituida por el valor de transacción
                      de la mercancía, determinado por los métodos de valoración
                      establecidos por el Título Octavo de la Ley y de este
                      reglamento, más los gastos de carga y descarga más el
                      costo de transporte y seguro hasta la aduana de frontera,
                      entendiéndose ésta como aduana de ingreso al país.
                    </Text>
                  </Text>
                </View>

                {/* TABLAS */}
                {anexoConverts.map((convert, index) => {
                  return (
                    <View
                      key={`anexo-${index}`}
                      wrap={false}
                      style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "flex-start",
                          marginVertical: 3,
                          color: "#5c5c5c",
                        }}
                      >
                        <View style={styles.contenedorTabla}>
                          <View style={styles.filaTabla}>
                            <View
                              style={[
                                styles.celdaEncabezado,
                                { backgroundColor: "#f5f5f5" },
                              ]}
                            >
                              <Text>NOTA DE VALOR POR ITEM TR200-18 PV</Text>
                            </View>
                          </View>
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaDatosIzquierda}>
                              <Text>Valor Fob</Text>
                            </View>
                            <View style={styles.celdaDatosDerecha}>
                              <Text> {convert.notaValor?.valorFob || 0} </Text>
                            </View>
                          </View>

                          <View style={styles.filaTabla}>
                            <View style={styles.celdaDatosIzquierda}>
                              <Text>Gastos en Origen</Text>
                            </View>
                            <View style={styles.celdaDatosDerecha}>
                              <Text>
                                {convert.notaValor?.gastosOrigen || 0}
                              </Text>
                            </View>
                          </View>

                          <View style={styles.filaTabla}>
                            <View style={styles.celdaDatosIzquierda}>
                              <Text>Flete 1</Text>
                            </View>
                            <View style={styles.celdaDatosDerecha}>
                              <Text>{convert.notaValor?.flete1 || 0}</Text>
                            </View>
                          </View>

                          <View style={styles.filaTabla}>
                            <View style={styles.celdaDatosIzquierda}>
                              <Text>Flete 2</Text>
                            </View>
                            <View style={styles.celdaDatosDerecha}>
                              <Text>{convert.notaValor?.flete2 || 0}</Text>
                            </View>
                          </View>

                          <View style={styles.filaTabla}>
                            <View style={styles.celdaDatosIzquierda}>
                              <Text>Seguro</Text>
                            </View>
                            <View style={styles.celdaDatosDerecha}>
                              <Text>{convert.notaValor?.seguro || 0}</Text>
                            </View>
                          </View>

                          <View style={styles.filaTabla}>
                            <View style={styles.celdaDatosIzquierda}>
                              <Text>ASPB / GASTOS PUERTOS</Text>
                            </View>
                            <View style={styles.celdaDatosDerecha}>
                              <Text>
                                {convert.notaValor?.aspbGastosPuertos || 0}
                              </Text>
                            </View>
                          </View>

                          <View style={styles.filaTabla}>
                            <View style={styles.celdaDatosIzquierda}>
                              <Text>OTROS G (BAF-HANDLING-THC)</Text>
                            </View>
                            <View style={styles.celdaDatosDerecha}>
                              <Text>{convert.notaValor?.otrosGastos || 0}</Text>
                            </View>
                          </View>

                          {/* Fila de totales en Dólar */}
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaTotalIzquierda}>
                              <Text>CIF FRONTERA DÓLAR</Text>
                            </View>
                            <View
                              style={[
                                styles.celdaTotalDerecha,
                                { textAlign: "right", padding: 3 },
                              ]}
                            >
                              <Text>
                                {convert.notaValor?.cifFronteraDolares || 0}
                              </Text>
                            </View>
                          </View>

                          {/* Fila de totales en Bolivianos */}
                          <View style={styles.filaTabla}>
                            <View
                              style={[
                                styles.celdaTotalIzquierda,
                                { borderBottom: "0px solid white" },
                              ]}
                            >
                              <Text>CIF FRONTERA BOLIVIANOS</Text>
                            </View>
                            <View
                              style={[
                                styles.celdaTotalDerecha,
                                {
                                  borderBottom: "0px solid white",
                                  textAlign: "right",
                                  padding: 3,
                                },
                              ]}
                            >
                              <Text>
                                {convert.notaValor?.cifFronteraBolivianos || 0}
                              </Text>
                            </View>
                          </View>
                        </View>
                        <View
                          style={[
                            styles.contenedorTabla,
                            { width: "55%", marginTop: 17 },
                          ]}
                        >
                          {/* Encabezado Principal */}
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaEncabezado}>
                              <Text>LIQUIDACION DE TRIBUTOS POR ITEM</Text>
                            </View>
                          </View>

                          {/* Encabezados de columnas */}
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaLiquidacionHeader}>
                              <Text>TIPO</Text>
                            </View>
                            <View style={styles.celdaLiquidacionHeader}>
                              <Text>BASE IMPONIBLE</Text>
                            </View>
                            <View style={styles.celdaLiquidacionHeader}>
                              <Text>TASA</Text>
                            </View>
                            <View style={styles.celdaLiquidacionHeaderFinal}>
                              <Text>IMPORTE BS.</Text>
                            </View>
                          </View>

                          {/* Filas de datos estáticas */}
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaLiquidacion}>
                              <Text>GA</Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.ga
                                  ?.baseImponible || 0}{" "}
                              </Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.ga?.tasa || 0}
                              </Text>
                            </View>
                            <View
                              style={[
                                styles.celdaLiquidacionDerecha,
                                { textAlign: "center" },
                              ]}
                            >
                              <Text>
                                {(
                                  (convert.liquidacionTributos?.ga
                                    ?.baseImponible || 0) *
                                  ((convert.liquidacionTributos?.ga?.tasa ||
                                    0) /
                                    100)
                                ).toFixed(2)}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaLiquidacion}>
                              <Text>IVA</Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.iva
                                  ?.baseImponible || 0}{" "}
                              </Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.iva?.tasa || 0}
                              </Text>
                            </View>
                            <View
                              style={[
                                styles.celdaLiquidacionDerecha,
                                { textAlign: "center" },
                              ]}
                            >
                              <Text>
                                {(
                                  (convert.liquidacionTributos?.iva
                                    ?.baseImponible || 0) *
                                  ((convert.liquidacionTributos?.iva?.tasa ||
                                    0) /
                                    100)
                                ).toFixed(2)}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaLiquidacion}>
                              <Text>ICE</Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.ice1
                                  ?.baseImponible || 0}{" "}
                              </Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.ice1?.tasa || 0}
                              </Text>
                            </View>
                            <View
                              style={[
                                styles.celdaLiquidacionDerecha,
                                { textAlign: "center" },
                              ]}
                            >
                              <Text>
                                {(
                                  (convert.liquidacionTributos?.ice1
                                    ?.baseImponible || 0) *
                                  ((convert.liquidacionTributos?.ice1?.tasa ||
                                    0) /
                                    100)
                                ).toFixed(2)}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaLiquidacion}>
                              <Text>ICE</Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.ice2
                                  ?.baseImponible || 0}{" "}
                              </Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.ice2?.tasa || 0}
                              </Text>
                            </View>
                            <View
                              style={[
                                styles.celdaLiquidacionDerecha,
                                { textAlign: "center" },
                              ]}
                            >
                              <Text>
                                {(
                                  (convert.liquidacionTributos?.ice2
                                    ?.baseImponible || 0) *
                                  ((convert.liquidacionTributos?.ice2?.tasa ||
                                    0) /
                                    100)
                                ).toFixed(2)}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaLiquidacion}>
                              <Text>MAB</Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.mab
                                  ?.baseImponible || 0}{" "}
                              </Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.mab?.tasa || 0}
                              </Text>
                            </View>
                            <View
                              style={[
                                styles.celdaLiquidacionDerecha,
                                { textAlign: "center" },
                              ]}
                            >
                              <Text>
                                {(
                                  (convert.liquidacionTributos?.mab
                                    ?.baseImponible || 0) *
                                  ((convert.liquidacionTributos?.mab?.tasa ||
                                    0) /
                                    100)
                                ).toFixed(2)}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaLiquidacion}>
                              <Text>IDH</Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.idh
                                  ?.baseImponible || 0}{" "}
                              </Text>
                            </View>
                            <View style={styles.celdaLiquidacion}>
                              <Text>
                                {convert.liquidacionTributos?.idh?.tasa || 0}
                              </Text>
                            </View>
                            <View
                              style={[
                                styles.celdaLiquidacionDerecha,
                                { textAlign: "center" },
                              ]}
                            >
                              <Text>
                                {(
                                  (convert.liquidacionTributos?.idh
                                    ?.baseImponible || 0) *
                                  ((convert.liquidacionTributos?.idh?.tasa ||
                                    0) /
                                    100)
                                ).toFixed(2)}
                              </Text>
                            </View>
                          </View>

                          {/* Fila del pie de tabla 1 */}
                          <View style={styles.filaTabla}>
                            <View style={[styles.celdaPieTabla]}>
                              <Text>Suma</Text>
                            </View>
                            <View style={styles.celdaTotalDerecha}>
                              <Text> {convert.liquidacionTributos?.suma} </Text>
                            </View>
                          </View>

                          {/* Fila del pie de tabla 2 */}
                          <View style={styles.filaTabla}>
                            <View style={styles.celdaPieTabla}>
                              <Text>Form Digital</Text>
                            </View>
                            <View style={styles.celdaTotalDerecha}>
                              <Text>100</Text>
                            </View>
                          </View>

                          {/* Fila del pie de tabla 3 */}
                          <View style={styles.filaTabla}>
                            <View
                              style={[
                                styles.celdaPieTabla,
                                { borderBottom: "0" },
                              ]}
                            >
                              <Text>Importe a Pagar</Text>
                            </View>
                            <View
                              style={[
                                styles.celdaTotalDerecha,
                                { borderBottom: "0" },
                              ]}
                            >
                              <Text>
                                {convert.liquidacionTributos?.suma + 100}
                              </Text>
                            </View>
                          </View>
                        </View>
                      </View>
                    );
                })}

                <View style={{ color: "#4a4a4a" }}>
                  <Text style={{ fontSize: 8, lineHeight: 1.5, marginTop: 20 }}>
                    <Text style={{ fontWeight: "bold" }}>1)</Text> Para el FLETE
                    1 se toma el 100% del flete maritimo
                  </Text>
                  <Text style={{ fontSize: 8, lineHeight: 1.5 }}>
                    <Text style={{ fontWeight: "bold" }}>2)</Text> Para el FLETE
                    2 se toma el valor del flete Terrestre hasta frontera, si no
                    esta por tramos considerar el 100% de flete
                  </Text>
                  <Text
                    style={{
                      fontSize: 8,
                      lineHeight: 1.5,
                      fontWeight: "semibold",
                      color: "black",
                    }}
                  >
                    <Text style={{ fontWeight: "bold" }}>3)</Text> Cuando la
                    operación de transporte se realice sin seguro, se presumirá
                    en concepto de la prima el importe equivalente al 2% del
                    valor FOB de la mercancía.
                  </Text>
                </View>
              </>
            ) : null })()}
          </View>

          {/* pie de pagina */}
          <View style={styles.footer} fixed>
            <View style={styles.footerRow}>
              <View
                style={{
                  flexDirection: "col",
                  width: "30%",
                  alignItems: "center",
                  alignSelf: "flex-end",
                  padding: 3,
                }}
              >
                <Text
                  style={{
                    ...styles.footerText,
                    fontWeight: "bold",
                    fontSize: 10,
                  }}
                >
                  Línea de Contacto
                </Text>
                <Text
                  style={{
                    ...styles.footerText,
                    fontWeight: "bold",
                    fontSize: 10,
                  }}
                >
                  3-3222987 (591) 74-667020
                </Text>
              </View>
              <View style={{ flexDirection: "column", width: "70%" }}>
                <View style={{ alignSelf: "flex-end" }}>
                  <Image
                    src={require("../../assets/LogoSCzPlus.png")}
                    style={{ width: 120, alignSelf: "center" }}
                  />
                </View>
                <Text
                  style={{
                    ...styles.footerText,
                    backgroundColor: "#009900",
                    color: "#fff",
                    padding: 3,
                    borderRadius: 4,
                    fontFamily: "Courier",
                    letterSpacing: -0.1,
                  }}
                >
                  Of. La Paz: Barrio Petrolero calle 12 #413 zona Villa Fatima
                </Text>
              </View>
            </View>
            <View>
              <Text
                style={{
                  ...styles.footerText,
                  backgroundColor: "#000000",
                  color: "#fff",
                  padding: 3,
                  borderRadius: 4,
                  fontFamily: "Courier",
                  letterSpacing: -0.5,
                }}
              >
                Of. Central Santa Cruz: Calle Cerebo Esquina Melao B. la
                Moliendita entre Av. Libertadores y tres pasos al frente 8vo
                Anillo comercial@g-scz.com
              </Text>
            </View>
          </View>
        </Page>
      </Document>
    );
  };

  return (
    <div className="flex flex-col items-end">
      <PDFDownloadLink
        document={<MyDoc data={data} />}
        fileName={`${data.codigoDeInterno || ""} - ${
          data.company.razonSocial
        }.pdf`}
        style={{
          textDecoration: "none",
          padding: "5px 10px",
          backgroundColor: "#4CAF50",
          color: "white",
          borderRadius: "5px",
          marginBottom: "5px",
        }}
      >
        {({ blob, url, loading, error }) =>
          loading ? "Cargando documento..." : "Descargar PDF"
        }
      </PDFDownloadLink>
      {data && (
        <PDFViewer
          style={{
            width: 900,
            height: window.innerHeight - 100,
          }}
          fileName={`${data.codigoDeInterno || ""} - ${
            data.company.razonSocial
          }.pdf`}
        >
          <MyDoc data={data} />
        </PDFViewer>
      )}
    </div>
  );
};

export default CustomsClearanceQuote;
